import { SyntaxNode } from '@lezer/common';

import { UnexpectedError } from '@watershed/errors/UnexpectedError';
import { UserInputError } from '@watershed/errors/UserInputError';

/**
 * @fileoverview Utility functions and classes for parsing DSL strings with Lezer
 */

/**
 * A position in the parsed input string.
 */
export type ParserPosition = Readonly<{
  input: string;
  from: number;
  to: number;
}>;

/**
 * Context object for parser operations based on Lezer concrete syntax trees.
 *
 * For efficiency Lezer's parse tree only stores offsets into the input, and not
 * e.g., substrings of the input. In order to get at those strings (e.g., to get
 * the text of an identifier, or to render an error message for an expression)
 * we need to separately pass around the original string. This context object is
 * mostly a wrapper around the input string, but with some helpers for common
 * operations.
 */
export class ParseContext {
  constructor(readonly input: string) {}

  /** Get the text of a node */
  slice(node: SyntaxNode): string {
    return this.input.slice(node.from, node.to);
  }

  /** Get the text of a node, as lowercase */
  sliceLower(node: SyntaxNode): string {
    return this.slice(node).toLowerCase();
  }

  /** Get the position of a node */
  position(node: SyntaxNode): ParserPosition {
    return {
      input: this.input,
      from: node.from,
      to: node.to,
    };
  }

  /**
   * Abort parsing with an exception when a state we believe is
   * impossible happens. These exceptions are *our* fault as authors of this
   * file, and likely indicates that there's a difference in expectations
   * between this file and the parser grammar.
   *
   * These are different than ParseErrors, which represent bad input.
   */
  impossible(what: string, node: SyntaxNode): never {
    throw new UnexpectedError(`impossible: bad ${what}: ${this.debug(node)}`);
  }

  /**
   * Get debug information about a node
   */
  debug(node: SyntaxNode): string {
    const text = this.slice(node);
    return `${text} (type=${node.name}, from=${node.from}, to=${node.to})`;
  }
}

/**
 * Parse errors are errors where the input string cannot be parsed due to an
 * error the input author (e.g., user) has made. The remedy is to correct the
 * input.
 *
 * These are different than impossible UnexpectedErrors, which represent
 * programming errors on our part.
 */
export class ParseError extends UserInputError {
  constructor(
    message: string,
    private ctx: ParseContext,
    readonly node: SyntaxNode
  ) {
    super(`Parse error: ${message}; while parsing ${ctx.debug(node)})`);
  }

  source(): string {
    return this.ctx.slice(this.node);
  }

  static isParseError(error: unknown): error is ParseError {
    return error instanceof Error && error.message.includes('Parse error');
  }
}

/**
 * Get the children of a node. Lezer has a less-than-ergonomic tree representation
 * (for efficiency). This helper converts things into a more normal array-of-children
 * representation.
 */
export function getNodeChildren(node: SyntaxNode): Array<SyntaxNode> {
  const out = [];
  let child = node.firstChild;
  while (child) {
    out.push(child);
    child = child.nextSibling;
  }
  return out;
}
